import type {
  PlayerStates,
  StartPositionsDataObject
} from '@/app/types'
import {
  minigameConfig,
  modes,
  playersManager,
  type PlayerInfo,
  type TournamentDataFromResultsRequest
} from '@powerplay/core-minigames'
import { player } from '../player'
import { Opponent } from './Opponent'
import { fakeOpponents } from './FakeOpponents'
import { gameConfig } from '@/app/config'

/** Trieda pre spravu protihracov */
export class OpponentsManager {

  /** Pole superov */
  private opponents: Opponent[] = []

  /** pocitadlo framov */
  private frameCounter = 0

  /** true ak v danom mode su opponenti */
  private isActive = true

  /** aktualna top3 hracov */
  private top3Players: string[] = []

  /** originalne data hracov */
  private originalPlayersData: PlayerInfo[] = []

  /**
   * Vrati pocet superov
   * @returns Pocet superov
   */
  public getOpponentsCount(): number {

    return this.opponents.length

  }

  /**
   * Vytvorenie superov
   * @param startData - Data
   * @param startingOrder - Poradie na starte
   * @param orderAnimationsPrepare - pole poradi pre prepare animacie
   */
  public create(
    startData: StartPositionsDataObject,
    startingOrder: StartPositionsDataObject,
    orderAnimationsPrepare: number[]
  ): void {

    if (!this.isActive) return

    playersManager.players.filter((playerInfo: PlayerInfo, index: number) => {

      return !playerInfo.playable && index <= gameConfig.numberOfOpponents

    }).forEach((playerInfo: PlayerInfo, index: number) => {

      this.opponents[index] = new Opponent(playerInfo.uuid, index + 1)
      this.opponents[index].create(
        startData[playerInfo.uuid],
        startingOrder[playerInfo.uuid] || 0,
        orderAnimationsPrepare[index + 1]
      )

    })

  }

  /**
   * aktualizovanie superov pred fyzikou
   */
  public updateBeforePhysics(): void {

    if (!this.isActive) return

    this.frameCounter += 1

    this.opponents.forEach((opponent) => {

      opponent.updateBeforePhysics(this.frameCounter)

    })

  }

  /**
   * aktualizovanie superov po fyzikou
   */
  public updateAfterPhysics(): void {

    if (!this.isActive) return

    const playersPositions = [{ uuid: player.uuid,
      percent: player.getActualPercentOnPath() }]

    this.opponents.forEach((opponent) => {

      opponent.updateAfterPhysics()
      playersPositions.push({ uuid: opponent.uuid,
        percent: opponent.getActualPercentOnPath() })

    })

    playersPositions.sort((a, b) => {

      if (a.percent < b.percent) return 1
      return -1

    })

    for (let i = 0; i < 3; i++) {

      if (playersPositions[i]) {

        this.top3Players.push(playersPositions[i].uuid)

      }

    }
    // this.top3Players = [playersPositions[0].uuid, playersPositions[1].uuid, playersPositions[2].uuid]

  }

  /**
   * Nastavenie stavu vsetkym superom
   * @param state - stav
   * @param maxRandomDelay - maximalna hodnota randomu delayu od 0
   */
  public setStateToAll(state: PlayerStates, maxRandomDelay = 0): void {

    this.opponents.forEach((opponent) => {

      opponent.setState(state, maxRandomDelay)

    })

  }

  /**
   * Nastavenie pozicie supera na starte
   */
  public setActualPercentOnStart(): void {

    this.opponents.forEach((opponent) => {

      opponent.worldEnvLinesManager.setActualPercentOnStart()

    })

  }

  /**
   * Odstartovanie vsetkych superov
   */
  public start(): void {

    if (!this.isActive) return

    this.frameCounter = 0
    this.opponents.forEach((opponent) => {

      opponent.start()

    })

  }

  /**
   * Aktualizovanie animacii superov
   * @param delta - delta pre animacie
   */
  public updateAnimations(delta: number): void {

    if (!this.isActive) return

    this.opponents.forEach((opponent) => {

      opponent.updateAnimations(delta)

    })

  }

  /**
   * Getter
   * @returns oppontnes
   */
  public getOpponents(): Opponent[] {

    return this.isActive ? this.opponents : []

  }

  /**
   * Getter
   * @returns uuids
   */
  public getOpponentsIds(): string[] {

    const uuids = [] as string[]
    playersManager.players.filter((playerInfo: PlayerInfo, index: number) => {

      return !playerInfo.playable && index <= gameConfig.numberOfOpponents

    }).forEach((playerInfo: PlayerInfo) => {

      uuids.push(playerInfo.uuid)

    })
    return uuids

  }

  /**
   * Nastavenie DNF pre vsetkych superov co nepresli cielom
   * @returns
   */
  public setDnfIfNotReachedFinish(): void {

    if (!this.isActive) return
    this.opponents.forEach((opponent) => {

      if (!opponent.isEnd) playersManager.setPlayerResultsById(opponent.uuid, minigameConfig.dnfValue)

    })

  }

  /**
   * Vratenie originalnych dat realnych hracov
   */
  public setOriginalData(): void {

    // pozor, tu menime aj hraca, nielen superov
    playersManager.players = []

    for (let i = 0; i <= this.originalPlayersData.length - 1; i += 1) {

      playersManager.players[i] = this.originalPlayersData[i]

      const resultArr = playersManager.players[i].resultsArr
      if (resultArr && resultArr[0] && resultArr[0].main === -1) {

        resultArr[0].main = minigameConfig.dnsValueAscending

      }

    }

  }

  /**
   * Prepisanie dat superov
   */
  public rewriteOpponentsData(): void {

    if (!modes.isTutorial() && !modes.isTrainingMode() && !modes.isDailyLeague() && !modes.isTournament()) return

    this.setFakeOpponentsData()

  }

  /**
   * Nastavenie fake dat pre tutorial a trening pre superov
   */
  private setFakeOpponentsData(): void {

    if (modes.isDailyLeague() || modes.isTournament()) {

      for (let i = 0; i <= playersManager.players.length - 1; i += 1) {

        this.originalPlayersData[i] = playersManager.players[i]

      }

    }

    this.setFakeData()

  }

  /**
   * Vratenie fake dat hracov
   */
  public setFakeData(): void {

    for (let i = 1; i <= gameConfig.numberOfOpponents; i += 1) {

      playersManager.players[i] = fakeOpponents[i - 1]

      if (modes.isDailyLeague() || modes.isTournament()) {

        playersManager.players[i].attribute.total = playersManager.getPlayer().attribute.total + 30

      } else {

        playersManager.players[i].attribute.total = playersManager.getPlayer().attribute.total

      }

    }

  }

  /**
   * Nastavenie dat o hracoch
   * @param dataFromCallback - data o hracoch
   */
  public setOpponentsDataFromSaveResults(dataFromCallback: TournamentDataFromResultsRequest): void {

    const newPlayersData: PlayerInfo[] = []
    let index = 0
    dataFromCallback.players.forEach((playerInfo) => {

      playerInfo.attribute = {
        base: 0,
        total: 0
      }

      if (playerInfo.uuid === player.uuid) playerInfo.playable = true
      if (playerInfo.resultsArr && playerInfo.resultsArr[0].main === -1) {

        playerInfo.resultsArr[0].main = minigameConfig.dnsValueAscending

      }

      // musime zarucit, ze hrac bude aj tu prvy
      if (!playerInfo.playable) index += 1
      newPlayersData[playerInfo.playable ? 0 : index] = playerInfo

    })

    playersManager.players = newPlayersData

    // musime este upravit data pre hraca, kedze sa vyssim priradenim dali na DNF
    playersManager.players[0].resultsArr = playersManager.players[0].resultsArrOriginal

  }

  /**
   * Aktualizovanie animacii superov
   */
  public reset(): void {

    if (!this.isActive) return

    this.opponents.forEach((opponent) => {

      opponent.reset()

    })

  }

}

export const opponentsManager = new OpponentsManager()
